/**
 * useDocumentMeta — client-side <title> + <meta description> updater for
 * routes that need correct SEO metadata in BOTH preview and production envs.
 *
 * Most pages get their <title> + meta tags from the build-time prerender
 * runtime injector (see /app/frontend/prerender.js). That table is baked at
 * build time though, so newly-added routes don't get correct metadata in
 * preview until the next production rebuild.
 *
 * This hook is the runtime safety net: it writes document.title and meta
 * description on mount. In production, the prerender injector runs first
 * and sets the same values; this hook runs after React mounts and re-sets
 * to the SAME values (no conflict). In preview, the injector falls through
 * (route not in baked table) and this hook handles it cleanly.
 *
 * Use sparingly — most pages should rely on seo-routes.js + prerender.
 */
import { useEffect } from 'react';

const CANONICAL_BASE = 'https://ascendforyou.net';

const setMeta = (attr, key, val) => {
  if (!val) return;
  const sel = `meta[${attr}="${key}"]`;
  let el = document.querySelector(sel);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', val);
};

// setLink intentionally removed — the hook no longer writes canonical tags
// at runtime. Canonical is owned by the build-time prerender.

/**
 * @param {object} meta
 * @param {string} meta.title - page <title>
 * @param {string} meta.description - meta description
 * @param {string} [meta.path] - route path used to build canonical (defaults to location.pathname)
 * @param {string} [meta.imageAlt] - og:image:alt
 */
export const useDocumentMeta = ({ title, description, path, imageAlt } = {}) => {
  useEffect(() => {
    if (!title && !description) return;
    const route = path || (typeof window !== 'undefined' ? window.location.pathname : '');
    const canonical = `${CANONICAL_BASE}${route === '/' ? '/' : route}`;

    if (title) document.title = title;
    setMeta('name', 'description', description);
    // INTENTIONALLY DO NOT mutate <link rel="canonical"> at runtime.
    // The build-time prerender (prerender.js + seo-routes.js) owns the
    // canonical URL for every route. Client-side rewrites here would race
    // with the prerender and produce duplicate-content signals — exactly
    // the bug the 2026-05-17 SEO audit flagged as P0.

    // Open Graph + Twitter — these are safe to set at runtime (no canonical
    // signaling) and useful for dynamic pages whose OG tags depend on
    // fetched data (e.g. score-share pages).
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('name', 'twitter:url', canonical);
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    if (imageAlt) {
      setMeta('property', 'og:image:alt', imageAlt);
      setMeta('name', 'twitter:image:alt', imageAlt);
    }
  }, [title, description, path, imageAlt]);
};

export default useDocumentMeta;
